import React from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  Typography, 
  Container, 
  IconButton, 
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Button,
  Divider
} from '@mui/material';
import { Close, ArrowForward, CalendarToday, Person } from '@mui/icons-material';

const Blog = ({ open, onClose }) => {
  const blogPosts = [
    {
      title: 'How AI is Transforming Enterprise Search',
      excerpt: 'Explore how artificial intelligence and machine learning are revolutionizing the way companies search through and utilize their knowledge bases.',
      author: 'Jane Doe',
      date: 'March 15, 2024',
      category: 'Technology',
      image: '/placeholder-blog-1.jpg'
    },
    {
      title: 'Measuring the ROI of Improved Knowledge Discovery',
      excerpt: 'Learn how to calculate the return on investment when implementing advanced search solutions in your organization.',
      author: 'John Smith',
      date: 'February 28, 2024',
      category: 'Business',
      image: '/placeholder-blog-2.jpg'
    },
    {
      title: 'Best Practices for Organizing Your Company Knowledge Base',
      excerpt: 'Discover proven strategies for structuring and maintaining your organizational knowledge for optimal searchability and accessibility.',
      author: 'Emily Chen',
      date: 'February 10, 2024',
      category: 'Productivity',
      image: '/placeholder-blog-3.jpg'
    }
  ];

  const categories = [
    'All', 'Technology', 'Business', 'Productivity', 'Case Studies', 'Company News'
  ];

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" color="#0d9488">
            Blog
          </Typography>
          <IconButton onClick={onClose} aria-label="close">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Container>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              Insights, updates, and stories from the Searcheble team
            </Typography>
            <Typography color="text.secondary">
              Stay up to date with the latest trends in search technology and knowledge management.
            </Typography>
          </Box>
          
          {/* Categories */}
          <Box sx={{ mb: 4, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {categories.map((category, index) => (
              <Chip 
                key={index}
                label={category}
                clickable
                sx={{ 
                  bgcolor: category === 'All' ? '#0d9488' : 'transparent',
                  color: category === 'All' ? 'white' : 'inherit',
                  borderColor: '#0d9488',
                  '&:hover': {
                    bgcolor: category === 'All' ? 'rgba(13, 148, 136, 0.8)' : 'rgba(13, 148, 136, 0.1)'
                  },
                  ...(category !== 'All' && { variant: 'outlined' })
                }}
              />
            ))}
          </Box>

          {/* Featured Post */}
          <Card sx={{ mb: 5, display: { md: 'flex' }, height: { md: 300 } }}>
            <CardMedia
              component="img"
              sx={{ width: { md: '40%' }, height: { xs: 200, md: '100%' } }}
              image="/placeholder-featured.jpg"
              alt="Featured blog post"
            />
            <CardContent sx={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <Box>
                <Chip 
                  label="Featured" 
                  size="small" 
                  sx={{ 
                    bgcolor: '#0d9488', 
                    color: 'white', 
                    mb: 1.5 
                  }} 
                />
                <Typography variant="h5" gutterBottom>
                  The Future of Search: 5 Predictions for the Next Decade
                </Typography>
                <Typography color="text.secondary" paragraph>
                  In this forward-looking article, we explore how search technology will evolve over the next ten years and how businesses can prepare for these changes. From voice search to contextual understanding, discover the innovations that will shape how we find and interact with information.
                </Typography>
              </Box>
              
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
                    <Person fontSize="small" sx={{ color: "#0d9488", mr: 0.5 }} />
                    <Typography variant="body2" color="text.secondary">
                      Michael Brown
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarToday fontSize="small" sx={{ color: "#0d9488", mr: 0.5 }} />
                    <Typography variant="body2" color="text.secondary">
                      March 20, 2024
                    </Typography>
                  </Box>
                </Box>
                
                <Button 
                  sx={{ 
                    color: '#0d9488',
                    '&:hover': {
                      backgroundColor: 'rgba(13, 148, 136, 0.1)'
                    }
                  }}
                  endIcon={<ArrowForward />}
                >
                  Read More
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Divider sx={{ my: 4 }} />

          {/* Recent Posts */}
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Recent Articles
          </Typography>
          
          <Grid container spacing={3}>
            {blogPosts.map((post, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardMedia
                    component="img"
                    height="140"
                    image={post.image || '/api/placeholder/400/320'}
                    alt={post.title}
                  />
                  <CardContent sx={{ flex: '1 1 auto' }}>
                    <Chip 
                      label={post.category} 
                      size="small" 
                      variant="outlined"
                      sx={{ 
                        borderColor: '#0d9488', 
                        color: '#0d9488', 
                        mb: 1.5 
                      }} 
                    />
                    <Typography variant="h6" gutterBottom>
                      {post.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {post.excerpt}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
                        <Person fontSize="small" sx={{ color: "#0d9488", mr: 0.5 }} />
                        <Typography variant="body2" color="text.secondary">
                          {post.author}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <CalendarToday fontSize="small" sx={{ color: "#0d9488", mr: 0.5 }} />
                      <Typography variant="body2" color="text.secondary">
                        {post.date}
                      </Typography>
                    </Box>
                    
                    <Button 
                      sx={{ 
                        color: '#0d9488',
                        mt: 'auto',
                        '&:hover': {
                          backgroundColor: 'rgba(13, 148, 136, 0.1)'
                        }
                      }}
                      endIcon={<ArrowForward />}
                    >
                      Read More
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              variant="contained"
              sx={{
                bgcolor: '#0d9488',
                '&:hover': { bgcolor: 'rgba(13, 148, 136, 0.8)' }
              }}
            >
              View All Posts
            </Button>
          </Box>
        </Container>
      </DialogContent>
    </Dialog>
  );
};

export default Blog;